// src/app/adjustments-admin/adjustments-admin.component.ts
import { Component } from '@angular/core';
import { DataserviceService } from '../dataservice.service';

@Component({
  selector: 'app-adjustments-admin',
  templateUrl: './adjustments-admin.component.html',
  styleUrls: ['./adjustments-admin.component.css']
})
export class AdjustmentsAdminComponent {
  adjustments: any[] = [];
  displayData: any[] = [];
  pageIndex = 1;
  pageSize = 20;
  form: any = {
    payer: { type: 'company', id: null, company_id: null },
    payee_user_id: null,
    amount: null,
    category: '',
    reason: '',
    notes: '',
    payout_date: null,
    recovery: { strategy: 'split', total_amount: null, schedule: [] },
    min_take_home: null,
    max_per_cycle: null,
    recovery_destination: 'original_payer',
    recovery_destination_user_id: null
  };
  isLoading = false;
  showCreateModal = false;
  companies: Array<{ id: string; name: string }> = [];

  constructor(private ds: DataserviceService) {}

  ngOnInit() {
    this.refresh();
    this.ds.getData('company').subscribe((resp: any) => {
      const rows = Array.isArray(resp)
        ? resp
        : Array.isArray(resp?.data)
          ? resp.data
          : [];
      this.companies = rows.map((c: any) => ({ id: c.id, name: c.name }));
    }, err => console.error('load companies error', err));
    // Populate users dropdown; wire this to your existing users endpoint
    this.ds.getData('users').subscribe((list: any[]) => {
      this.users = (list || []).map(u => ({ id: u.id, label: `${u.first_name} ${u.last_name}`.trim() }));
    });
  }

  refresh() {
    this.isLoading = true;
    this.ds.getData('payplans/adjustments').subscribe(r => {
      this.adjustments = Array.isArray(r) ? r : (Array.isArray(r?.data) ? r.data : []);
      this.applyFiltersSort();
      this.isLoading = false;
    }, _ => this.isLoading = false);
  }

  openCreate() {
    this.resetForm();
    this.showCreateModal = true;
  }

  handleCreateCancel() {
    this.showCreateModal = false;
  }

  handleCreateOk() {
    this.isLoading = true;
    this.ds.post('payplans/adjustments', this.form).subscribe(_ => {
      this.isLoading = false;
      this.showCreateModal = false;
      this.refresh();
    }, _ => this.isLoading = false);
  }

  resetForm() {
    this.form = {
      payer: { type: 'company', id: null, company_id: null },
      payee_user_id: null,
      amount: null,
      category: '',
      reason: '',
      notes: '',
      payout_date: null,
      recovery: { strategy: 'split', total_amount: null, schedule: [] },
      min_take_home: null,
      max_per_cycle: null,
      recovery_destination: 'original_payer',
      recovery_destination_user_id: null
    };
    this.splitIntervals = 2;
    this.splitStartDate = null;
  }

  users: Array<{ id: string; label: string }> = [];
  splitIntervals = 2;
  splitStartDate: Date | null = null;
  searchTerm = '';
  sortKey: string = 'created_at';
  sortOrder: 'ascend' | 'descend' = 'descend';

  userLabel(id?: string) {
    if (!id) return '';
    const u = this.users.find(x => x.id === id);
    return u ? u.label : id;
  }

  companyLabel(id?: string) {
    if (!id) return '';
    const c = this.companies.find(x => x.id === id);
    return c ? c.name : id;
  }

  private normalizeDate(d: any): number {
    if (!d) return 0;
    const dt = typeof d === 'string' || d instanceof Date ? new Date(d) : new Date();
    return dt.getTime() || 0;
  }

  private rowText(a: any): string {
    const cat = a?.details?.category || '';
    const notes = a?.details?.notes || '';
    const payee = this.userLabel(a?.details?.payee_user_id);
    const payer = a?.details?.payer?.type === 'user' ? this.userLabel(a?.details?.payer?.id) : this.companyLabel(a?.details?.payer?.company_id);
    const strat = a?.strategy || a?.details?.recovery?.strategy || '';
    const amt = Number(a?.amount || 0).toFixed(2);
    const remaining = Number(a?.remaining_amount ?? a?.details?.recovery?.remaining_amount ?? 0).toFixed(2);
    return `${cat} ${notes} ${payee} ${payer} ${strat} ${amt} ${remaining}`.toLowerCase();
  }

  private remainingValue(a: any): number {
    const val = Number(a?.remaining_amount ?? a?.details?.recovery?.remaining_amount ?? 0);
    return isNaN(val) ? 0 : val;
  }

  displayStatus(a: any): string {
    const hasRecovery = !!(a?.strategy || a?.details?.recovery?.strategy);
    if (hasRecovery && this.remainingValue(a) <= 0) return 'paid in full';
    return a?.status || '';
  }

  applyFiltersSort() {
    const term = (this.searchTerm || '').toLowerCase().trim();
    const filtered = (this.adjustments || []).filter(a => !term || this.rowText(a).includes(term));

    const key = this.sortKey;
    const dir = this.sortOrder === 'ascend' ? 1 : -1;
    const getVal = (a: any) => {
      switch (key) {
        case 'amount': return Number(a.amount || 0);
        case 'status': return String(a.status || '');
        case 'payout_date': return this.normalizeDate(a?.details?.payout_date);
        case 'planned_date': return this.normalizeDate(a?.planned_date);
        case 'remaining': return Number(a?.remaining_amount ?? a?.details?.recovery?.remaining_amount ?? 0);
        case 'type': return String(a?.entry_kind || 'payout');
        case 'payee': return this.userLabel(a?.details?.payee_user_id);
        case 'payer': return (a?.details?.payer?.type === 'user') ? this.userLabel(a?.details?.payer?.id) : this.companyLabel(a?.details?.payer?.company_id);
        case 'strategy': return a?.strategy || a?.details?.recovery?.strategy || '';
        case 'created_at':
        default:
          return this.normalizeDate(a?.created_at);
      }
    };
    this.displayData = filtered.sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    this.pageIndex = 1;
  }

  onSearchChange() {
    console.log('searchTerm: ', this.searchTerm);
    this.applyFiltersSort();
  }

  setSort(key: string) {
    if (this.sortKey === key) {
      this.sortOrder = this.sortOrder === 'ascend' ? 'descend' : 'ascend';
    } else {
      this.sortKey = key;
      this.sortOrder = key === 'created_at' ? 'descend' : 'ascend';
    }
    this.applyFiltersSort();
  }

  sortOrderIf(key: string) {
    return this.sortKey === key ? this.sortOrder : null;
  }

  onSort(key: string, order: 'ascend' | 'descend' | null) {
    this.sortKey = key;
    this.sortOrder = (order || 'ascend');
    this.applyFiltersSort();
  }

  remainingToAllocate() {
    const scheduled = (this.form.recovery.schedule || []).reduce((s: number, it: any) => s + Number(it.amount || 0), 0);
    return Math.max(0, Number(this.form.amount || 0) - scheduled);
  }

  recomputeRemaining() {
    // just triggers change detection; remaining is computed above
  }

  autoSplit() {
    const n = Math.max(1, Number(this.splitIntervals || 1));
    const total = Number(this.form.amount || 0);
    const base = Math.floor((total / n) * 100) / 100;
    const schedule = [];
    const start = this.splitStartDate ? new Date(this.splitStartDate) : new Date();
    let allocated = 0;
    for (let i = 0; i < n; i++) {
      const d = new Date(start);
      d.setMonth(d.getMonth() + i); // monthly by default
      let amt = i === n - 1 ? Number((total - allocated).toFixed(2)) : base;
      allocated = Number((allocated + amt).toFixed(2));
      schedule.push({ date: d, amount: amt, applied: false });
    }
    this.form.recovery = this.form.recovery || {};
    this.form.recovery.schedule = schedule;
  }
}